// Phase 83s — Notification Sheet (Bottom Sheet style) for VĐV bell button.
// Hiển thị tối đa 30 thông báo gần nhất + nút "Đã đọc tất cả".
// Click 1 thông báo → mark as read + navigate đến link (nếu có).
// Phase 84.M.25 — Wrap entire sheet in a React Portal mounted to
// `document.body` so the `position: fixed` rules escape any ancestor
// that creates a containing block (sticky headers with `backdrop-blur`,
// motion-wrapped pages, transformed containers, …). Without this the
// sheet was being clamped INSIDE the sticky `<header>` of BXH / Trận đấu
// pages → only 78 px of the drawer showed at the very top of the screen.
// Phase 84.M.52 — Hiển thị nút "Đồng ý" inline cho thông báo loại
// "rating.proposed" + popup modal trách nhiệm (giống ở Cá nhân).
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { m as motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, X, Check, CheckCircle, Trophy, Clock, ShieldCheck, Diamond, CalendarBlank, Spinner } from "@phosphor-icons/react";
import { athleteRatingProposals } from "../../../lib/athleteApi";
import { formatApiErrorDetail } from "../../../lib/authApi";

const TYPE_ICONS = {
  "registration.approved": ShieldCheck,
  "registration.rejected": X,
  "registration.payment_paid": CheckCircle,
  "match.completed": Trophy,
  "match.started": Clock,
  "bracket.ready": Diamond,
  "schedule.published": CalendarBlank,
  "rating.proposed": ShieldCheck,
  "rating.self_verified_proposal": ShieldCheck,
  default: Bell,
};

const TYPE_COLORS = {
  "registration.approved": "#A8FF00",
  "registration.rejected": "#FF4D4F",
  "registration.payment_paid": "#A8FF00",
  "match.completed": "#FFB300",
  "match.started": "#34D8FF",
  "bracket.ready": "#FFD700",
  "schedule.published": "#34D8FF",
  "rating.proposed": "#FCD34D",
  "rating.self_verified_proposal": "#FCD34D",
  default: "#FF8A00",
};

const RATING_NOTIF_TYPES = new Set(["rating.proposed", "rating.self_verified_proposal"]);

function timeAgo(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const diff = Math.floor((Date.now() - t) / 1000);
    if (diff < 60) return `${diff}s trước`;
    if (diff < 3600) return `${Math.floor(diff / 60)} phút trước`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} giờ trước`;
    return new Date(iso).toLocaleString("vi-VN", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

export default function NotificationSheet({ open, onClose, items = [], unreadCount = 0, onMarkRead, onMarkAllRead }) {
  const navigate = useNavigate();
  // Phase 84.M.52 — Modal state cho nút "Đồng ý" trên notification rating.proposed.
  const [pendingAcceptNotif, setPendingAcceptNotif] = useState(null);
  const [acceptAck, setAcceptAck] = useState(false);
  const [acceptBusy, setAcceptBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const handleItemClick = (n) => {
    if (!n.is_read && n.id) onMarkRead?.(n.id);
    if (n.link) {
      onClose?.();
      navigate(n.link);
    }
  };

  // Phase 84.M.52 — Accept rating proposal trực tiếp từ thông báo.
  const openAcceptModal = (n) => {
    setPendingAcceptNotif(n);
    setAcceptAck(false);
  };

  const closeAcceptModal = () => {
    if (acceptBusy) return;
    setPendingAcceptNotif(null);
    setAcceptAck(false);
  };

  const confirmAccept = async () => {
    if (!pendingAcceptNotif || !acceptAck) return;
    const proposalId = pendingAcceptNotif.source_data?.proposal_id;
    if (!proposalId) {
      toast.error("Thông báo không chứa mã đề xuất hợp lệ");
      return;
    }
    setAcceptBusy(true);
    try {
      await athleteRatingProposals.decide(proposalId, "accepted", true);
      toast.success("Đã chấp nhận điểm trình do BTC đề xuất");
      if (!pendingAcceptNotif.is_read && pendingAcceptNotif.id) {
        onMarkRead?.(pendingAcceptNotif.id);
      }
      setPendingAcceptNotif(null);
      setAcceptAck(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Lỗi xử lý");
    } finally {
      setAcceptBusy(false);
    }
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm"
            onClick={onClose}
            data-testid="notification-sheet-backdrop"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className="fixed bottom-0 left-0 right-0 z-[81] bg-[#0F1623] rounded-t-3xl border-t border-white/8 max-h-[88dvh] flex flex-col"
            data-testid="notification-sheet"
          >
            <div className="px-5 pt-3 pb-2 flex items-center justify-between border-b border-white/8">
              <div className="flex-1 flex justify-center">
                <div className="w-10 h-1 bg-white/15 rounded-full" />
              </div>
            </div>
            <div className="px-5 pt-3 pb-3 flex items-center justify-between">
              <div>
                <h3 className="text-[16px] font-black text-white">Thông báo</h3>
                <p className="text-[11px] text-white/55 mt-0.5">
                  {unreadCount > 0 ? `${unreadCount} chưa đọc` : "Tất cả đã đọc"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={onMarkAllRead}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-[#FF8A00] hover:bg-[#FF8A00]/10 transition"
                    data-testid="notification-mark-all-read"
                  >
                    <Check size={11} weight="bold" /> Đánh dấu đã đọc
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition"
                  data-testid="notification-sheet-close"
                  aria-label="Đóng"
                >
                  <X size={14} weight="bold" className="text-white/65" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-3 pb-6">
              {items.length === 0 ? (
                <div className="text-center py-12">
                  <Bell size={32} className="mx-auto text-white/20 mb-2" />
                  <div className="text-[13px] font-bold text-white/55">Chưa có thông báo</div>
                  <div className="text-[11px] text-white/35 mt-1">Bạn sẽ thấy thông báo khi BTC duyệt giải đấu.</div>
                </div>
              ) : (
                <ul className="space-y-1.5" data-testid="notification-list">
                  {items.map((n) => {
                    const Icon = TYPE_ICONS[n.type] || TYPE_ICONS.default;
                    const color = TYPE_COLORS[n.type] || TYPE_COLORS.default;
                    return (
                      <li key={n.id}>
                        <button
                          type="button"
                          onClick={() => handleItemClick(n)}
                          className={`w-full text-left rounded-xl border px-3 py-2.5 flex items-start gap-3 transition active:scale-[0.99] ${
                            n.is_read
                              ? "border-white/5 bg-white/2 hover:bg-white/4"
                              : "border-[#FF8A00]/30 bg-[#FF8A00]/8 hover:bg-[#FF8A00]/12"
                          }`}
                          data-testid={`notification-item-${n.id}`}
                        >
                          <div
                            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                            style={{ background: `${color}18`, border: `1px solid ${color}40` }}
                          >
                            <Icon size={15} weight="bold" style={{ color }} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <div className="text-[12.5px] font-bold text-white truncate">
                                {n.title}
                              </div>
                              {!n.is_read && (
                                <span className="shrink-0 w-2 h-2 rounded-full bg-[#FF8A00] mt-1.5" />
                              )}
                            </div>
                            {n.body && (
                              <div className="text-[11.5px] text-white/65 mt-0.5 line-clamp-2">
                                {n.body}
                              </div>
                            )}
                            <div className="text-[10px] text-white/40 font-medium mt-1">
                              {timeAgo(n.created_at)}
                            </div>
                            {/* Phase 84.M.52 — Quick Accept button cho rating proposal notif. */}
                            {RATING_NOTIF_TYPES.has(n.type) && n.source_data?.proposal_id && !n.source_data?.accepted && (
                              <div
                                className="flex items-center gap-2 mt-2"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openAcceptModal(n);
                                  }}
                                  className="flex-1 h-8 rounded-lg bg-gradient-to-r from-amber-400 to-orange-400 text-[#0A0F1C] text-[11px] font-black inline-flex items-center justify-center gap-1 active:scale-95 transition"
                                  data-testid={`notif-accept-rating-${n.id}`}
                                >
                                  <CheckCircle size={11} weight="bold" /> Đồng ý
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleItemClick(n);
                                  }}
                                  className="flex-1 h-8 rounded-lg bg-white/8 hover:bg-white/12 border border-white/10 text-white/80 text-[11px] font-bold inline-flex items-center justify-center gap-1 transition"
                                  data-testid={`notif-detail-${n.id}`}
                                >
                                  Xem chi tiết
                                </button>
                              </div>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </motion.div>
          {/* Phase 84.M.52 — Accept-from-notif modal (trách nhiệm). */}
          {pendingAcceptNotif && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[82] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
              onClick={closeAcceptModal}
              data-testid="notif-accept-modal"
            >
              <motion.div
                initial={{ y: 60, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 60, opacity: 0 }}
                className="w-full sm:max-w-md bg-[#0F1623] border border-amber-400/35 rounded-t-3xl sm:rounded-3xl p-5 space-y-3.5"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-amber-500/15 border border-amber-400/40 flex items-center justify-center shrink-0">
                    <ShieldCheck size={20} weight="bold" className="text-amber-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-black text-white">Xác nhận điểm trình</div>
                    <div className="text-[12px] text-white/65 mt-0.5">{pendingAcceptNotif.body}</div>
                  </div>
                </div>
                <label className="flex items-start gap-2 text-[12px] text-white/85 leading-snug cursor-pointer select-none rounded-xl bg-white/4 border border-white/8 p-2.5">
                  <input
                    type="checkbox"
                    checked={acceptAck}
                    onChange={(e) => setAcceptAck(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#FF8A00] rounded"
                    data-testid="notif-accept-modal-ack"
                  />
                  <span>
                    Tôi cam kết khai báo trình độ trung thực và chịu hoàn toàn trách nhiệm về quyết định này.
                  </span>
                </label>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    disabled={acceptBusy}
                    onClick={closeAcceptModal}
                    className="flex-1 h-11 rounded-2xl text-[12.5px] font-bold bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 transition disabled:opacity-40"
                    data-testid="notif-accept-modal-cancel"
                  >
                    Để sau
                  </button>
                  <button
                    type="button"
                    disabled={acceptBusy || !acceptAck}
                    onClick={confirmAccept}
                    className="flex-1 h-11 rounded-2xl text-[12.5px] font-black bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] text-white shadow-[0_8px_24px_rgba(255,138,0,0.45)] disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                    data-testid="notif-accept-modal-confirm"
                  >
                    {acceptBusy ? <Spinner size={12} className="animate-spin" /> : <CheckCircle size={13} weight="bold" />}
                    Đồng ý
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}
